import { PRESETS } from '../config/presets';
import type { ConvertResult, JobConfig } from './types';
import { fileToRgba } from './image';
import { buildCoverRevealFrames, buildPseudoStaticFrames } from './frames';
import { encodeApng } from './apng-encode';
import { formatMetaSummary, readApngMeta } from './apng-meta';

function baseName(filename: string): string {
  const i = filename.lastIndexOf('.');
  return i > 0 ? filename.slice(0, i) : filename;
}

function sanitizeName(name: string): string {
  return name.replace(/[^\w\u4e00-\u9fff.-]+/g, '_').slice(0, 80) || 'output';
}

export interface PipelineInput {
  contentFile: File;
  coverFile?: File | null;
  config: JobConfig;
}

/**
 * 主线程管线：解码 → 缩放 → 组帧 → UPNG 编码 → patch → 结果
 */
export async function convertToApng(input: PipelineInput): Promise<ConvertResult> {
  const { contentFile, coverFile, config } = input;
  const preset = PRESETS[config.preset];
  const scale = config.scale > 0 ? config.scale : 1;

  if (preset.requireCover && !coverFile) {
    throw new Error('当前预设需要选择封面图');
  }

  const content = await fileToRgba(contentFile, { scale });
  const pixels = content.width * content.height;
  if (pixels > 12_000_000) {
    console.warn('大图像素较多，编码可能较慢或占用较多内存');
  }

  let frames;
  if (coverFile && (preset.requireCover || config.preset === 'manual')) {
    const cover = await fileToRgba(coverFile, {
      targetWidth: content.width,
      targetHeight: content.height,
    });
    frames = buildCoverRevealFrames(cover, content);
  } else {
    frames = buildPseudoStaticFrames(content);
  }

  const delays = config.delays;
  const loop = Math.max(0, Math.trunc(config.loop));
  const bytes = encodeApng(frames, [delays[0], delays[1]], loop);
  const meta = readApngMeta(bytes);

  if (!meta.actl || meta.actl.numFrames < 2) {
    throw new Error('编码结果不是有效双帧 APNG，请重试或更换图片');
  }

  const tag = preset.tag;
  const filename = `${sanitizeName(baseName(contentFile.name))}${tag}${config.outputExt}`;
  const metaSummary = formatMetaSummary(meta, [delays[0], delays[1]]);

  return {
    bytes,
    width: content.width,
    height: content.height,
    frameCount: meta.actl.numFrames,
    loop: meta.actl.numPlays,
    delays,
    filename,
    metaSummary,
  };
}

export function downloadBytes(bytes: Uint8Array, filename: string): void {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  const blob = new Blob([copy.buffer], { type: 'image/png' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 延迟 revoke，确保下载启动
  setTimeout(() => URL.revokeObjectURL(url), 4_000);
}
